// assets/eos-fiyat-farki.js
import { Component } from '@theme/component';

/**
 * Annotates variant option choices with the real price difference they cause.
 *
 * Nothing here is authored and nothing here is calculated: the block computes
 * `min(price of the variants carrying this value) - min(price of all variants)`
 * in Liquid and ships each label already run through the `money` filter, so the
 * currency, the decimal separator and the presentment market all match every
 * other price on the page. This file only places the text and keeps it placed.
 *
 * Horizon re-renders the variant picker by morphing its DOM, which silently
 * drops anything we injected. A MutationObserver re-applies the annotations,
 * and every write is idempotent so a re-apply is a no-op when nothing changed.
 *
 * @typedef {{name: string, value: string, label: string}} DeltaRow
 *
 * @extends Component
 */
class EosFiyatFarki extends Component {
  /** @type {Element | null} Stable ancestor that outlives a picker re-render. */
  #scope = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {Map<string, Map<string, string>>} Option name -> value -> label. */
  #labels = new Map();
  /** @type {number | null} */
  #pending = null;

  /**
   * Block settings live on the inner wrapper: Shopify generates the custom
   * element tag itself, so block markup cannot put attributes on it.
   *
   * @returns {DOMStringMap}
   */
  get #config() {
    return this.querySelector('.eos-fiyat-farki')?.dataset ?? this.dataset;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#labels = this.#readLabels();
    if (this.#labels.size === 0) return;

    // Watch a stable ancestor rather than the picker itself: Horizon sometimes
    // morphs the picker's insides and sometimes swaps the whole element, and an
    // observer bound to a replaced node would simply stop firing.
    this.#scope = this.closest('[data-eos-step]') ?? this.closest('.shopify-section') ?? document.body;
    this.#observer = new MutationObserver(() => this.#scheduleApply());
    this.#apply();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#pending !== null) clearTimeout(this.#pending);
    this.#pending = null;
  }

  /**
   * The picker is a sibling inside the same wizard step, so look inside our own
   * scope first. A product page can hold several pickers (quick add, related
   * products) and only this product's may be annotated.
   *
   * @returns {Element | null}
   */
  #findPicker() {
    return this.#scope?.querySelector('variant-picker') ?? null;
  }

  /**
   * Reads the server-rendered labels into "option name -> value -> label".
   *
   * @returns {Map<string, Map<string, string>>}
   */
  #readLabels() {
    const labels = new Map();

    /** @type {DeltaRow[]} */
    let rows;
    try {
      rows = JSON.parse(this.#config.eosDeltas ?? '[]');
    } catch {
      // Malformed data must leave the page exactly as the server rendered it.
      return labels;
    }
    if (!Array.isArray(rows)) return labels;

    for (const row of rows) {
      if (!row?.name || row.value == null || !row.label) continue;
      let byValue = labels.get(row.name);
      if (!byValue) labels.set(row.name, (byValue = new Map()));
      byValue.set(String(row.value), String(row.label));
    }

    return labels;
  }

  /** @returns {string} Label for this choice, empty when it costs nothing extra. */
  #labelFor(optionName, value) {
    if (!optionName || value == null) return '';
    return this.#labels.get(optionName)?.get(value) ?? '';
  }

  /**
   * Morphs arrive in bursts; coalesce them into one pass. A timeout rather than
   * rAF because rAF callbacks do not fire reliably in the verification browser.
   */
  #scheduleApply() {
    if (this.#pending !== null) return;
    this.#pending = setTimeout(() => {
      this.#pending = null;
      this.#apply();
    }, 0);
  }

  /** Writes every annotation, then re-arms the observer. */
  #apply() {
    const scope = this.#scope;
    if (!scope) return;

    // Our own writes would otherwise re-trigger the observer forever.
    this.#observer?.disconnect();
    try {
      const picker = this.#findPicker();
      for (const input of picker?.querySelectorAll('input[type="radio"][data-option-name]') ?? []) {
        this.#annotateButton(input);
      }
      for (const option of picker?.querySelectorAll('option[data-option-name]') ?? []) {
        this.#annotateDropdownOption(option);
      }
    } finally {
      this.#observer?.observe(scope, { childList: true, subtree: true, characterData: true });
    }
  }

  /** @param {HTMLInputElement} input */
  #annotateButton(input) {
    const label = input.closest('label');
    if (!label) return;

    const text = this.#labelFor(input.dataset.optionName, input.value);
    const existing = label.querySelector('.eos-fiyat-farki__delta');

    if (!text) {
      existing?.remove();
      if (input.getAttribute('aria-describedby')?.startsWith('EosFiyatFarki-')) {
        input.removeAttribute('aria-describedby');
      }
      return;
    }

    const id = `EosFiyatFarki-${this.#config.blockId ?? 'x'}-${input.dataset.inputId ?? input.value}`;

    const node = existing ?? document.createElement('span');
    if (!existing) {
      node.className = 'eos-fiyat-farki__delta';
      const valueText = label.querySelector('[data-key="variant-option-text"]');
      valueText ? valueText.after(node) : label.append(node);
    }
    // The radio carries an explicit aria-label, so label text is not part of
    // its accessible name; describedby is what actually gets announced.
    if (node.id !== id) node.id = id;
    if (node.textContent !== text) node.textContent = text;
    if (input.getAttribute('aria-describedby') !== node.id) {
      input.setAttribute('aria-describedby', node.id);
    }
  }

  /**
   * Dropdowns cannot hold elements, so the delta goes into the option text.
   *
   * The base label is recovered by stripping the suffix we last wrote, never by
   * caching the text on first sight: the picker renders its own
   * "- Mevcut değil" suffix into the same text node and flips it as
   * availability changes, so a cached copy would freeze a stale reading.
   *
   * @param {HTMLOptionElement} option
   */
  #annotateDropdownOption(option) {
    const current = option.textContent ?? '';
    const written = option.dataset.eosDeltaSuffix;
    // Liquid leaves the option text padded with newlines; trim so appending a
    // suffix cannot produce "40 cm\n (+100,00 TL)".
    const base = (written && current.endsWith(written) ? current.slice(0, -written.length) : current).trim();

    const text = this.#labelFor(option.dataset.optionName, option.value);
    const next = text ? `${base} (${text})` : base;

    if (current !== next) option.textContent = next;
    if (text) {
      option.dataset.eosDeltaSuffix = ` (${text})`;
    } else {
      delete option.dataset.eosDeltaSuffix;
    }
  }
}

if (!customElements.get('eos-fiyat-farki-component')) {
  customElements.define('eos-fiyat-farki-component', EosFiyatFarki);
}
